/**
 * Pure policy for surviving playback failures without operator intervention.
 *
 * Two failure modes need different handling:
 * - A single bad item (corrupt file, transient network blip) → skip ahead.
 * - Systemic failure (a tab resumed from overnight sleep with every presigned
 *   URL expired) → skipping just storms through the queue; the player must
 *   instead resync for fresh URLs and pause the advance loop.
 */

export type MediaFailureKind =
  | "expired-url"
  | "missing"
  | "network"
  | "decode"
  | "other";

/**
 * HTTP statuses that mean the object will never appear, no matter how many
 * fresh presigned URLs we mint for it.
 */
function isMissingStatus(status: number | null | undefined): boolean {
  return status === 404 || status === 410;
}

/** Rolling window in which consecutive failures are counted. */
export const FAILURE_WINDOW_MS = 30_000;
/** Hard ceiling on consecutive failures before the budget trips. */
export const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Classify a media element error. MediaError codes 2 (network) and 4
 * (src-not-supported — how browsers surface a 403 on an expired presigned
 * URL) are treated as an expired URL when the item's `urlExpiresAt` has
 * passed; code 3 is a decode fault in the media itself.
 */
export function classifyMediaError(input: {
  mediaErrorCode: number | null | undefined;
  urlExpiresAt?: Date | string | number | null;
  /**
   * The HTTP status, when something upstream of the media element actually saw
   * it. An `HTMLMediaElement` reports a 404 and a 403 identically (code 4), so
   * this is the only way the web side can tell "gone" from "stale".
   */
  httpStatus?: number | null;
  now?: number;
}): MediaFailureKind {
  const { mediaErrorCode, urlExpiresAt, httpStatus, now = Date.now() } = input;
  // A definitive answer from the transport outranks every guess below.
  if (isMissingStatus(httpStatus)) return "missing";
  if (mediaErrorCode === 3) return "decode";
  if (mediaErrorCode === 2 || mediaErrorCode === 4) {
    if (urlExpiresAt != null) {
      const expires = new Date(urlExpiresAt).getTime();
      if (Number.isFinite(expires) && expires < now) return "expired-url";
    }
    return "network";
  }
  return "other";
}

/**
 * How a native shell noticed a failure. Expo's audio/video players do not
 * expose an `HTMLMediaElement.error.code`, so the native side classifies from
 * WHERE the failure came from instead of from an error code:
 *
 *  - `error-state`  the player reported an error/failed playback state;
 *  - `stall`        the stall watchdog tripped (loaded, no forward progress);
 *  - `load-error`   fetching the media into the offline cache threw.
 */
export type NativeFailureSignal = "error-state" | "stall" | "load-error";

/**
 * Native twin of `classifyMediaError`, producing the SAME vocabulary so a
 * native `playback.failed` row and a web one are comparable.
 *
 * An expired presigned URL wins over everything: it is both the most common
 * real cause (a venue that slept through the night) and the only one with a
 * different fix (resync for fresh URLs, not skip). Everything else stays
 * deliberately coarse — the raw `signal` rides along on the metric payload, so
 * guessing "network" for a stall we cannot actually attribute would add a
 * confident-sounding label without adding a fact.
 */
export function classifyNativeFailure(input: {
  signal: NativeFailureSignal;
  urlExpiresAt?: Date | string | number | null;
  /** Native knows this exactly: the cache download throws with the status. */
  httpStatus?: number | null;
  now?: number;
}): MediaFailureKind {
  const { signal, urlExpiresAt, httpStatus, now = Date.now() } = input;
  if (isMissingStatus(httpStatus)) return "missing";
  if (urlExpiresAt != null) {
    const expires = new Date(urlExpiresAt).getTime();
    if (Number.isFinite(expires) && expires < now) return "expired-url";
  }
  // A download that threw is a fetch fault: no bytes ever arrived.
  if (signal === "load-error") return "network";
  return "other";
}

/**
 * Counts consecutive playback failures within a rolling window. `record` on
 * every failure, `reset` on any successful playback start; `tripped` says
 * whether skipping ahead has stopped being a viable recovery strategy.
 */
export class FailureBudget {
  private failures: number[] = [];
  private readonly windowMs: number;
  private readonly maxFailures: number;

  // Parameter properties are avoided: pure player libs run under Node
  // strip-types, which rejects that syntax.
  constructor(windowMs = FAILURE_WINDOW_MS, maxFailures = MAX_CONSECUTIVE_FAILURES) {
    this.windowMs = windowMs;
    this.maxFailures = maxFailures;
  }

  /** Record a failure at `now`; returns the count inside the rolling window. */
  record(now = Date.now()): number {
    this.failures = this.failures.filter((t) => now - t < this.windowMs);
    this.failures.push(now);
    return this.failures.length;
  }

  /** Failures currently inside the window (as of the last `record`). */
  get count(): number {
    return this.failures.length;
  }

  reset(): void {
    this.failures = [];
  }

  /**
   * True when every plausible candidate has failed: the threshold is the
   * queue length capped at `maxFailures`, floored at 1 so an empty queue
   * (single-asset slot) still trips on its first failure.
   */
  tripped(queueLength: number): boolean {
    const threshold = Math.max(1, Math.min(queueLength, this.maxFailures));
    return this.failures.length >= threshold;
  }
}

/** Attempts on one item before it is written off as permanently unplayable. */
export const DEAD_ITEM_ATTEMPTS = 3;

/**
 * Remembers items that have failed so often they cannot be worth another try.
 *
 * WHY THIS EXISTS. A media asset whose object is missing from storage fails
 * forever, and every layer above it was built to assume failures are transient:
 * the failure budget trips (for a single-item interrupt slot the threshold is
 * literally 1), the player halts, requests a force-resync, gets a *fresh
 * presigned URL for the same absent object*, retries, and repeats. The venue
 * goes silent and the console sits in `error` — for a problem no retry can fix.
 *
 * Once an item is dead, the player skips it WITHOUT spending failure budget, so
 * one bad asset can no longer take the whole channel down with it. A successful
 * play clears the item: if the object is later uploaded, it heals on its own.
 *
 * Bounded so a long-running kiosk cannot accumulate ids without limit.
 */
export class DeadItemRegistry {
  private readonly attempts = new Map<string, number>();
  private readonly maxAttempts: number;
  private readonly capacity: number;

  constructor(maxAttempts = DEAD_ITEM_ATTEMPTS, capacity = 500) {
    this.maxAttempts = maxAttempts;
    this.capacity = capacity;
  }

  /**
   * Record one failed attempt. `permanent` short-circuits the count: a 404 is
   * already proof, so there is nothing to learn from trying twice more.
   */
  recordFailure(itemId: string | null | undefined, permanent = false): boolean {
    if (!itemId) return false;
    const next = permanent
      ? this.maxAttempts
      : (this.attempts.get(itemId) ?? 0) + 1;
    // Insertion-ordered eviction: drop the oldest id, never the one being
    // written, so a hot failing item cannot evict itself.
    if (!this.attempts.has(itemId) && this.attempts.size >= this.capacity) {
      const oldest = this.attempts.keys().next();
      if (!oldest.done) this.attempts.delete(oldest.value);
    }
    this.attempts.set(itemId, next);
    return next >= this.maxAttempts;
  }

  /** True once the item has exhausted its attempts — skip it, do not halt. */
  isDead(itemId: string | null | undefined): boolean {
    if (!itemId) return false;
    return (this.attempts.get(itemId) ?? 0) >= this.maxAttempts;
  }

  /** A successful play proves the item is fine again. */
  clear(itemId: string | null | undefined): void {
    if (itemId) this.attempts.delete(itemId);
  }

  reset(): void {
    this.attempts.clear();
  }

  /** Dead item ids, for telemetry/debug surfaces. */
  deadIds(): string[] {
    const dead: string[] = [];
    for (const [id, count] of this.attempts) {
      if (count >= this.maxAttempts) dead.push(id);
    }
    return dead;
  }
}

/**
 * Index every hydrated media item in a sync payload by id. Presigned URLs
 * rotate on every fetch while `syncVersion` fingerprints content only, so a
 * same-version payload still carries fresh URLs that must replace the stale
 * item objects cached in rotations and queues. First occurrence wins,
 * matching `mediaAssetById` lookup order.
 */
export function collectSyncItemsById<T extends { id: string }>(
  sync: { playlists: Array<{ items: T[] }> } | null | undefined,
): Map<string, T> {
  const byId = new Map<string, T>();
  if (!sync) return byId;
  for (const playlist of sync.playlists) {
    for (const item of playlist.items) {
      if (!byId.has(item.id)) byId.set(item.id, item);
    }
  }
  return byId;
}

/**
 * Swap stale item references for their freshly signed counterparts by id.
 * Items absent from the map (e.g. synthetic tone entries) are kept as-is;
 * returns the input array unchanged when nothing was swapped.
 */
export function remapItemsById<T extends { id: string }>(
  items: T[],
  byId: ReadonlyMap<string, T>,
): T[] {
  let changed = false;
  const next = items.map((item) => {
    const fresh = byId.get(item.id);
    if (fresh && fresh !== item) {
      changed = true;
      return fresh;
    }
    return item;
  });
  return changed ? next : items;
}
